/*
  This class creates and manages a Three.js canvas with interactive media (video) 
  textures. It handles the camera, scene, renderer, raycasting for mouse 
  interaction, and resizing logic. The class also renders the media using a 
  Media class instance, which applies custom shaders and deformations to 
  the video element.
*/

import * as THREE from 'three'
import { Dimensions, Size } from '../../../constants/types'
import Media from './media'
import gsap from 'gsap'
import GridRenderSingleton from '../../../ultils/GridRender'

export default class Canvas {
  element: HTMLCanvasElement        // The HTML canvas element where the Three.js scene is rendered
  scene!: THREE.Scene               // The Three.js scene containing all objects and lights
  camera!: THREE.PerspectiveCamera  // Perspective camera for the scene
  renderer!: THREE.WebGLRenderer    // The renderer for displaying the scene on the canvas
  sizes!: Size                      // Dimensions of the media plane
  dimensions!: Dimensions           // Dimensions of the window (for resizing and pixel ratio)
  time: number                      // Timestamp for rendering and animation
  isMobile: number
  clock!: THREE.Clock               // Clock used for managing time-based rendering updates
  raycaster!: THREE.Raycaster       // Raycaster for detecting mouse interactions with 3D objects
  mouse!: THREE.Vector2             // Mouse coordinates, normalized for Three.js interactions
  media!: Media                     // Media instance that manages the video texture and interaction

  constructor(isMobile: number = 0) {
    // Grabbing the canvas element by its ID
    this.element = document.getElementById('webgl') as HTMLCanvasElement
    this.time = 0
    this.isMobile = isMobile

    this.createClock()
    this.createScene()
    this.createCamera()
    this.createRenderer()
    this.setSizes()
    this.createRayCaster()
    this.addEventListeners()
    this.createMedia()
    this.createGridReveal()
    this.render()
  }

  // Create the Three.js scene
  createScene() {
    this.scene = new THREE.Scene()
  }

  // Create and position the camera in the scene
  createCamera() {
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 100)
    this.scene.add(this.camera)
    this.camera.position.z = 10
  }

  // Set up the WebGL renderer and adjust its settings
  createRenderer() {
    this.dimensions = {
      width: window.innerWidth,
      height: window.innerHeight,
      pixelRatio: Math.min(2, window.devicePixelRatio),  // Set a max pixel ratio of 2 for high-DPI displays
    }

    // Create the WebGLRenderer with transparency
    this.renderer = new THREE.WebGLRenderer({ canvas: this.element, alpha: true })
    this.renderer.setSize(this.dimensions.width, this.dimensions.height)
    this.renderer.render(this.scene, this.camera)

    this.renderer.setPixelRatio(this.dimensions.pixelRatio)
  }

  setSizes() {
    if (this.isMobile)
    {
      // original scaling code - worked for mobile but has obvious stretching issues on wide monitors
      let fovRadians = this.camera.fov * (Math.PI / 180)
      let height = this.camera.position.z * Math.tan(fovRadians / 2) * 2
      let width = height * this.camera.aspect
      
      this.sizes = {
        width: width,
        height: height,
      }

    } else {
      // desktop scaling - appears to scale well on regular and wide monitors, as well as tablets
      const fovRad = this.camera.fov * (Math.PI / 180)
      const viewHeight = this.camera.position.z * Math.tan(fovRad / 2) * 2
      const viewWidth = viewHeight * this.camera.aspect
    
      const targetAspect = 16 / 9; // The website uses Showreel.mp4 which is 16:9 for desktop.
      const viewAspect = viewWidth / viewHeight
    
      let width, height
    
      if (viewAspect > targetAspect) {
        // View is wider than 16:9 — crop top/bottom, keep width
        width = viewWidth
        height = width / targetAspect
        // console.log(`isMobile: ${this.isMobile}, viewAspect: ${viewAspect}, View is wider than 16:9 — crop top/bottom, keep width`);
        
      } else {
        // View is taller than 16:9 — crop sides, keep height
        height = viewHeight
        width = height * targetAspect
        // console.log(`isMobile: ${this.isMobile}, viewAspect: ${viewAspect}, View is taller than 16:9 — crop sides, keep height`);
      }
      
      // console.log(`width: ${width}, height: ${height}, canvas AR: ${width / height}`)
      this.sizes = {
        width: width,
        height: height,
      }
    }
  }

  // Initialize the clock to track elapsed time
  createClock() {
    this.clock = new THREE.Clock()
  }

  // Create the raycaster and initialize mouse coordinates for interaction
  createRayCaster() {
    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()
  }

  // Handle mouse movement and interact with objects in the scene using raycasting
  onMouseMove(event: MouseEvent | TouchEvent) {
    
    let u, v; // Compute UV coordinates based on cursor position relative to window size
    // Animate the mouse coordinates using GSAP for smooth interaction
    if (event instanceof MouseEvent) {
      u = (event.clientX / window.innerWidth);
      v = -(event.clientY / window.innerHeight) + 1.0; // Window coordinate system positions (0,0) at the top left.
                                                       // uv coordinates has (0,0) on the bottom left. i.e, y-axis is inversed.
      gsap.to(this.mouse, 0.2, {
        x: (event.clientX / window.innerWidth) * 2 - 1, // Normalize mouse X position to range -1 to 1
        y: -(event.clientY / window.innerHeight) * 2 + 1 // Normalize mouse Y position to range -1 to 1
      })
    }
    else if (event instanceof TouchEvent) {
      // console.log(`touch: (${event.touches[0].clientX}, ${event.touches[0].clientY})`)
      u = (event.touches[0].clientX / window.innerWidth);
      v = -(event.touches[0].clientY / window.innerHeight) + 1.0;

      gsap.to(this.mouse, 0.2, {
        x: (event.touches[0].clientX / window.innerWidth) * 2 - 1, // Normalize mouse X position to range -1 to 1
        y: -(event.touches[0].clientY / window.innerHeight) * 2 + 1 // Normalize mouse Y position to range -1 to 1
      })
    }

    // Compute UV based on touch position and window size
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)
    const target = intersects[0]
    if (target && 'material' in target.object) {
      const targetMesh = target.object as THREE.Mesh
      // target mesh is the plane we are rendering our video to
      if (this.media.mesh === targetMesh && target.uv) {
        // GX: uv here seems to expect the range [0, 1] with 0 being left/bottom and 1 being right/top
        // console.log(`three UV: (${target.uv.x}, ${target.uv.y}), my UV: (${u}, ${v})`);
        // this.media.onMouseMove(target.uv)              // original code - appears to be displaced from actual cursor position    
        this.media.onMouseMove(new THREE.Vector2(u, v));
      }
    }
  }

  // Add event listeners for mouse movement and window resizing
  addEventListeners() {
    window.addEventListener('mousemove', this.onMouseMove.bind(this))
    window.addEventListener('touchmove', this.onMouseMove.bind(this))
    window.addEventListener('resize', this.onResize.bind(this))
  }

  // Handle window resize events and adjust the canvas size, camera, and media dimensions
  onResize() {
    this.dimensions = {
      width: window.innerWidth,
      height: window.innerHeight,
      pixelRatio: Math.min(2, window.devicePixelRatio),
    }

    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()
    this.setSizes()

    this.renderer.setPixelRatio(this.dimensions.pixelRatio)
    this.renderer.setSize(this.dimensions.width, this.dimensions.height)
    this.renderer.setClearColor(0x000000, 1)

    this.media.onResize(this.sizes)
  }

  // Create the media instance, binding a video element to the media class for rendering
  createMedia() {
    // Grab the video element
    const image = document.querySelector('.title-video-player') as HTMLVideoElement
    const media = new Media({
      element: image,
      scene: this.scene,
      sizes: this.sizes,
      renderer: this.renderer,
      mouse: this.mouse,
      isMobile: this.isMobile
    })

    this.media = media
  }

  createGridReveal() {
    const gridInstance = GridRenderSingleton.getInstance();
    // const dimX = gridInstance.dimX;
    // const dimY = gridInstance.dimY;
    gridInstance.context = this.renderer.getContext();
  }

  // Main render loop for updating the scene
  render() {
    this.time = this.clock.getElapsedTime()

    this.media.render(this.time)

    this.renderer.render(this.scene, this.camera)
  }
}